import { atom } from 'jotai'
import { addEdge, Edge } from '@xyflow/react'
import { dequal } from 'dequal'

import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

import {
  presentationToApiPresentation,
  validatePresentation,
  ValidationResults
} from 'Automations/nodeDefinitions'
import { getNodeHandleId } from 'Automations/nodeDefinitions/helpers/nodeHandleId'
import { NodeType } from 'Automations/nodeDefinitions/types'
import { collectReachableNodeIds, getPrimaryOutputHandleId } from './helpers'
import {
  AutomationsPresentationV1,
  AutomationsPresentationV1Edge,
  AutomationsPresentationV1Node
} from './types'
import { SidebarMode } from './components'
import { UNTITLED_AUTOMATION_NAME } from './constants'

export {
  UNDO,
  REDO,
  RESET,
  presentationAtom,
  presentationHistoryAtom,
  resetPresentationHistoryAtom,
  lastSavedPresentationAtom,
  canUndoAtom,
  canRedoAtom,
  undoAtom,
  redoAtom,
  draggingNodesAtom,
  historyNavigationCountAtom
} from './presentation.atoms'

export {
  connectionDropAtom,
  connectionDropPreviewTypeAtom,
  connectionDropPreviewNodeAtom,
  addNodeFromConnectionAtom
} from './connectionDrop.atoms'

import {
  presentationAtom,
  draggingNodesAtom,
  lastSavedPresentationAtom
} from './presentation.atoms'

export {
  expandedNodeTypesAtom,
  toggleNodeTypeExpandedAtom,
  collapsedNodeSizesAtom,
  expandedNodeSizesAtom,
  expandTransitionProgressAtom
} from './nodeExpansion.atoms'
export type { NodeSize } from './nodeExpansion.atoms'

export {
  resolvedNodeDefinitionsAtom,
  useResolvedNodeDefinitions,
  getResolvedNodeDefinitions,
  usePopulateResolvedNodeDefinitions
} from 'Automations/nodeDefinitions/resolvedNodeDefinitions.atoms'

import {
  resolvedNodeDefinitionsAtom,
  getResolvedNodeDefinitions
} from 'Automations/nodeDefinitions/resolvedNodeDefinitions.atoms'

type AutomationVersion = Dashboardv99990101Api.Automations.AutomationVersion

/**
 * DEPRECATED
 */
export const isAutomationActiveAtom = atom<boolean>(false)

/**
 * Automation / automation version
 */
export const automationNameAtom = atom<string>('')
export const formattedAutomationNameAtom = atom(get => {
  const name = get(automationNameAtom)
  return name || UNTITLED_AUTOMATION_NAME
})
export const automationDescriptionAtom = atom<string>('')

export const editingVersionAtom = atom<AutomationVersion | undefined>(undefined)
export const editingVersionIdAtom = atom<string | undefined>(get => {
  return get(editingVersionAtom)?.id
})

export const activeVersionAtom = atom<AutomationVersion | undefined>()
export const activeVersionIdAtom = atom<string | undefined>(get => {
  return get(activeVersionAtom)?.id
})

/**
 * Metadata change tracking
 */
export const lastSavedNameAtom = atom<string>('')
export const lastSavedDescriptionAtom = atom<string>('')

export const hasMetadataChangedAtom = atom<boolean>(get => {
  return (
    get(automationNameAtom) !== get(lastSavedNameAtom) ||
    get(automationDescriptionAtom) !== get(lastSavedDescriptionAtom)
  )
})

export const hasPresentationChangedAtom = atom<boolean>(get => {
  const presentation = get(presentationAtom)
  const lastSavedPresentation = get(lastSavedPresentationAtom)

  if (!presentation || !lastSavedPresentation) return false

  return !dequal(
    presentationToApiPresentation(presentation),
    presentationToApiPresentation(lastSavedPresentation)
  )
})

export const hasAnyChangesAtom = atom<boolean>(get => {
  return get(hasPresentationChangedAtom) || get(hasMetadataChangedAtom)
})

export const nodesAtom = atom(
  get => {
    return (
      get(draggingNodesAtom) ??
      get(versionPreviewPresentationAtom)?.nodes ??
      get(presentationAtom)?.nodes ??
      []
    )
  },
  (get, set, update: AutomationsPresentationV1Node[]) => {
    const versionPreview = get(versionPreviewAtom)
    if (versionPreview) return

    const presentation = get(presentationAtom)
    if (!presentation) return

    const definitions = getResolvedNodeDefinitions(get)

    const validSourceHandles = new Set(
      update.flatMap(node => {
        const def = definitions[node.type as NodeType]

        if (def?.handleMapping) {
          return Object.keys(
            def.handleMapping({ config: node.data as any })
          ).map(key =>
            getNodeHandleId({
              nodeId: node.id,
              type: 'source',
              handleMapping: key
            })
          )
        }

        return [getNodeHandleId({ nodeId: node.id, type: 'source' })]
      })
    )

    const prunedEdges = presentation.edges.filter(edge => {
      return !!edge.sourceHandle && validSourceHandles.has(edge.sourceHandle)
    })

    return set(presentationAtom, {
      ...presentation,
      nodes: update,
      edges: prunedEdges
    })
  }
)

export const addNodeAtom = atom(
  null,
  (
    get,
    set,
    params: {
      nodeType: NodeType
      config?: Record<string, unknown>
      position?: { x: number; y: number }
    }
  ) => {
    const presentation = get(presentationAtom)
    if (!presentation) return

    const definitions = getResolvedNodeDefinitions(get)

    const { nodeType, config, position } = params

    const maxZIndex = presentation.nodes.reduce(
      (max, node) => Math.max(max, (node as any).zIndex ?? 0),
      0
    )

    return set(presentationAtom, {
      ...presentation,
      nodes: [
        ...presentation.nodes,
        {
          id: crypto.randomUUID(),
          type: nodeType,
          position: position ?? { x: 0, y: 0 },
          data: {
            ...definitions[nodeType].defaultConfig,
            ...(config ?? {})
          },
          zIndex: maxZIndex + 1
        } as any
      ]
    })
  }
)

/**
 * Set after an inline insert so the canvas can shift the downstream subtree
 * once the new node has been measured (see applyInsertedNodeShiftAtom). Holds
 * the new node plus the original edge endpoints (for direction + reachability).
 */
export const pendingInsertedNodeShiftAtom = atom<{
  nodeId: string
  sourceId: string
  targetId: string
} | null>(null)

/**
 * Insert a node inline on an existing edge: split the edge in two and place
 * the new node where the edge's old midpoint was. The new node is spliced as
 * upstream → new → downstream; for branching nodes (Switch/Review) the
 * downstream connection is wired to the node's primary output, leaving
 * remaining branches for the user to connect. The downstream subtree is then
 * shifted to make room for the new node's body (applyInsertedNodeShiftAtom),
 * so each new connector ends up roughly half the original edge.
 */
export const insertNodeOnEdgeAtom = atom(
  null,
  (
    get,
    set,
    params: {
      edgeId: string
      nodeType: NodeType
      config?: Record<string, unknown>
      /** Measured width of the source node, to keep the input edge visible. */
      sourceWidth?: number
    }
  ) => {
    if (get(versionPreviewAtom)) return

    const presentation = get(presentationAtom)
    if (!presentation) return

    const definitions = getResolvedNodeDefinitions(get)

    const { edgeId, nodeType, config, sourceWidth = 0 } = params

    const edge = presentation.edges.find(e => e.id === edgeId)
    if (!edge) return

    const sourceNode = presentation.nodes.find(node => node.id === edge.source)
    const targetNode = presentation.nodes.find(node => node.id === edge.target)

    const newNodeId = crypto.randomUUID()
    const data = {
      ...definitions[nodeType].defaultConfig,
      ...(config ?? {})
    }

    const maxZIndex = presentation.nodes.reduce(
      (max, node) => Math.max(max, (node as any).zIndex ?? 0),
      0
    )

    // Place the node at the edge's midpoint, nudged forward by half the source
    // node's width so the input edge clears the source and stays visible at
    // roughly half the original edge's length.
    const position = ((): { x: number; y: number } => {
      if (!sourceNode || !targetNode) {
        return (sourceNode ?? targetNode)?.position ?? { x: 0, y: 0 }
      }

      const dx = targetNode.position.x - sourceNode.position.x
      const dy = targetNode.position.y - sourceNode.position.y
      const length = Math.hypot(dx, dy)
      const unit =
        length === 0 ? { x: 0, y: 0 } : { x: dx / length, y: dy / length }

      return {
        x:
          (sourceNode.position.x + targetNode.position.x) / 2 +
          (unit.x * sourceWidth) / 2,
        y:
          (sourceNode.position.y + targetNode.position.y) / 2 +
          (unit.y * sourceWidth) / 2
      }
    })()

    const newNode = {
      id: newNodeId,
      type: nodeType,
      position,
      data,
      zIndex: maxZIndex + 1
    } as AutomationsPresentationV1Node

    const newNodeTargetHandle = getNodeHandleId({
      nodeId: newNodeId,
      type: 'target'
    })
    const newNodeSourceHandle = getPrimaryOutputHandleId({
      nodeId: newNodeId,
      handleMapping: definitions[nodeType].handleMapping?.({
        config: data as any
      })
    })

    // Reuse addEdge so the spliced edges get the same id scheme as edges
    // created by dragging a connection (onConnect).
    const edges = addEdge(
      {
        source: newNodeId,
        sourceHandle: newNodeSourceHandle,
        target: edge.target,
        targetHandle: edge.targetHandle ?? null
      },
      addEdge(
        {
          source: edge.source,
          sourceHandle: edge.sourceHandle ?? null,
          target: newNodeId,
          targetHandle: newNodeTargetHandle
        },
        presentation.edges.filter(e => e.id !== edgeId) as Edge[]
      )
    )

    set(presentationAtom, {
      ...presentation,
      nodes: [...presentation.nodes, newNode],
      edges
    })

    set(pendingInsertedNodeShiftAtom, {
      nodeId: newNodeId,
      sourceId: edge.source,
      targetId: edge.target
    })
  }
)

/**
 * Shift the inserted node's downstream subtree away along the original edge
 * direction by the new node's measured width, making room for its body so the
 * two new connectors render at roughly half the original edge each. Runs once
 * the node has been measured; clears the pending shift either way.
 */
export const applyInsertedNodeShiftAtom = atom(
  null,
  (get, set, params: { width: number }) => {
    const pending = get(pendingInsertedNodeShiftAtom)
    if (!pending) return

    set(pendingInsertedNodeShiftAtom, null)

    const presentation = get(presentationAtom)
    if (!presentation) return

    const sourceNode = presentation.nodes.find(n => n.id === pending.sourceId)
    const targetNode = presentation.nodes.find(n => n.id === pending.targetId)
    if (!sourceNode || !targetNode) return

    const dx = targetNode.position.x - sourceNode.position.x
    const dy = targetNode.position.y - sourceNode.position.y
    const length = Math.hypot(dx, dy)
    if (length === 0 || params.width <= 0) return

    const shift = {
      x: (dx / length) * params.width,
      y: (dy / length) * params.width
    }

    const downstreamIds = collectReachableNodeIds({
      startId: pending.targetId,
      edges: presentation.edges,
      direction: 'downstream'
    })

    const nodes = presentation.nodes.map(node =>
      downstreamIds.has(node.id)
        ? {
            ...node,
            position: {
              x: node.position.x + shift.x,
              y: node.position.y + shift.y
            }
          }
        : node
    )

    set(presentationAtom, { ...presentation, nodes })
  }
)

export const deleteNodeAtom = atom(null, (get, set, nodeId: string) => {
  const presentation = get(presentationAtom)
  if (!presentation) return

  const selectedNodeIds = get(selectedNodeIdsAtom)

  if (selectedNodeIds.includes(nodeId)) {
    set(
      selectedNodeIdsAtom,
      selectedNodeIds.filter(id => id !== nodeId)
    )
  }

  return set(presentationAtom, {
    ...presentation,
    nodes: presentation.nodes.filter(node => node.id !== nodeId)
  })
})

export const edgesAtom = atom(
  get => {
    return (
      get(versionPreviewPresentationAtom)?.edges ??
      get(presentationAtom)?.edges ??
      []
    )
  },
  (get, set, update: AutomationsPresentationV1Edge[]) => {
    const versionPreview = get(versionPreviewAtom)
    if (versionPreview) return

    const presentation = get(presentationAtom)
    if (!presentation) return

    return set(presentationAtom, {
      ...presentation,
      edges: update
    })
  }
)

/**
 * Config editing
 */
export const selectedNodeIdsAtom = atom<string[]>([])
export const selectedEdgesAtom = atom<Edge[]>([])
export const selectedNodeFieldAtom = atom<
  { nodeId: string; fieldId: string } | undefined
>(undefined)
export const focusedSwitchGroupAtom = atom<string | undefined>(undefined)

/**
 * Validation
 *
 * Derived synchronously from the live presentation and the resolved node-
 * definition registry. Recomputes on every presentation edit, so readers
 * (the canvas error UI, the publish gate) always see results for the current
 * graph — no async effect, no stale-window race. Empty until the registry is
 * resolved.
 */
export const validationResultsAtom = atom<ValidationResults>(get => {
  const presentation = get(presentationAtom)
  const definitions = get(resolvedNodeDefinitionsAtom)

  if (!presentation || !definitions) return {}

  return validatePresentation(presentation, definitions)
})
export const showValidationResultsAtom = atom<boolean>(false)
export const validationPopoverKeyAtom = atom<number>(0)

/**
 * RBAC
 */
export const hasWritePermissionAtom = atom(false)

/**
 * UI
 */
export const showAddNodeToolbarAtom = atom(get => {
  return !get(isEditingDisabledAtom)
})

export const isConfigFormExpandedAtom = atom(false)

// Whether the user has toggled the config form's expanded state since the
// current node was selected. Used to gate the expand/collapse transition so it
// only animates on a real toggle — not on the layout shift that happens when a
// node config first mounts (which would otherwise produce an unwanted
// transition, e.g. the first reminder node added to a canvas).
export const configFormExpandInteractedAtom = atom(false)

export const currentSidebarModeAtom = atom<SidebarMode | undefined>(undefined)

export const isEditingDisabledAtom = atom<boolean>(get => {
  /**
   * Make the canvas read-only (e.g. don't allow nodes to be selected, moved, configured) when the user
   * lacks RBAC permissions, or another version of the automation is being previewed
   */
  return !!get(versionPreviewAtom) || !get(hasWritePermissionAtom)
})

/**
 * Version preview
 */
export const versionPreviewAtom = atom<AutomationVersion | undefined>(undefined)
export const versionPreviewIdAtom = atom(get => get(versionPreviewAtom)?.id)
export const versionPreviewPresentationAtom = atom<
  AutomationsPresentationV1 | undefined
>(get => {
  return get(versionPreviewAtom)?.presentation as
    | AutomationsPresentationV1
    | undefined
})
